import { QK } from '@/js/consts/query-keys';
import { useGetMyAccount } from '@/js/hooks/user';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, Ref } from 'vue';
import {
    approveAgents,
    getAnonymizedAgents,
    getApprovedAgents,
    getAgentsWaitingForApproval,
    revokeApproval,
} from './agentAnonymizationApi';

export const useQueryGetAgentsWaitingForApproval = (
    tenantId: Ref<string>,
    isActive: Ref<boolean>,
    page: Ref<number>,
    pageSize: Ref<number>
) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(
        () => !!tenantId.value && !!account.value?.backOfficeApiBaseUrl && isActive.value
    );

    return useQuery({
        queryKey: computed(() => [
            QK.AGENT_ANONYMIZATION.WAITING_FOR_APPROVAL,
            tenantId.value,
            page.value,
            pageSize.value,
        ]),
        queryFn: () => getAgentsWaitingForApproval(tenantId.value, page.value, pageSize.value),
        enabled,
    });
};

export const useQueryGetApprovedAgents = (tenantId: Ref<string>, isActive: Ref<boolean>) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(
        () => !!tenantId.value && !!account.value?.backOfficeApiBaseUrl && isActive.value
    );

    return useQuery({
        queryKey: computed(() => [QK.AGENT_ANONYMIZATION.APPROVED, tenantId.value]),
        queryFn: () => getApprovedAgents(tenantId.value),
        enabled,
    });
};

export const useQueryGetAnonymizedAgents = (
    tenantId: Ref<string>,
    isActive: Ref<boolean>,
    page: Ref<number>,
    pageSize: Ref<number>
) => {
    const { data: account } = useGetMyAccount();
    const enabled = computed(
        () => !!tenantId.value && !!account.value?.backOfficeApiBaseUrl && isActive.value
    );

    return useQuery({
        queryKey: computed(() => [
            QK.AGENT_ANONYMIZATION.ANONYMIZED,
            tenantId.value,
            page.value,
            pageSize.value,
        ]),
        queryFn: () => getAnonymizedAgents(tenantId.value, page.value, pageSize.value),
        enabled,
    });
};

export const useMutationApproveAgents = (tenantId: Ref<string>) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (agentIds: string[]) => approveAgents(tenantId.value, agentIds),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QK.AGENT_ANONYMIZATION.WAITING_FOR_APPROVAL, tenantId.value],
            });
            queryClient.invalidateQueries({
                queryKey: [QK.AGENT_ANONYMIZATION.APPROVED, tenantId.value],
            });
        },
    });
};

export const useMutationRevokeApproval = (tenantId: Ref<string>) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (agentId: string) => revokeApproval(tenantId.value, agentId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QK.AGENT_ANONYMIZATION.WAITING_FOR_APPROVAL, tenantId.value],
            });
            queryClient.invalidateQueries({
                queryKey: [QK.AGENT_ANONYMIZATION.APPROVED, tenantId.value],
            });
        },
    });
};
